import { IconLayer } from '@deck.gl/layers';
import { center, geometry } from '@turf/turf';
import anime from 'animejs';
import createPathLayer from 'components/Shared/PathLayer';
import { MAP_TRANSPORT_ICONS } from 'constants/map';
import { easePolyInOut } from 'd3-ease';
import { distanceTo } from 'geolocation-utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FlyToInterpolator, LinearInterpolator, } from 'react-map-gl';
import { useTheme } from 'styled-components';
import { ModeOfTransport, } from 'types/supplyChain.types';
import { MapType } from 'types/types';
import { getMapCenterAndZoom } from 'utils/geography';
import { useActiveContext } from '../Context/ActiveContext';
import { SiteType } from 'types/site.types';
const NEARBY_DISTANCE = 100000; // 100 km
const SUPER_NEARBY_DISTANCE = 40000; // 40 km
const CLOSEST_ZOOM = 11;
const MINIMUM_ZOOM = 7;
export const MAP_ANIMATION = {
    SHORT: 1500,
    LONG: 2500,
};
const useMapViewport = ({ width = 300, isSummaryMap, isIntersecting, mapLoaded, mapType, }) => {
    const { activeStepIndex, activeStep, activeStepActivity, steps, allStepActivityLinks, allStepActivities, isBatchTracingActive, } = useActiveContext();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [layers, setLayers] = useState();
    const prevIndex = useRef(activeStepIndex);
    const { palette } = useTheme();
    const activeStepCenter = useMemo(() => {
        if (!activeStep)
            return { center: { lat: 0, lng: 0 }, zoom: 1 };
        const { center, zoom } = getMapCenterAndZoom(activeStep.chainStepActivities.map(({ activity: { site } }) => site.siteType === SiteType.CLUSTER
            ? site.centroidCoordinates
            : site.locationCoordinates), width);
        return { center, zoom };
    }, [activeStep]);
    const [viewport, setViewport] = useState({
        latitude: activeStepCenter.center.lat,
        longitude: activeStepCenter.center.lng,
        zoom: 1,
        bearing: 0,
        pitch: 0,
    });
    const distanceToPrevious = useMemo(() => {
        var _a;
        if (activeStepIndex === prevIndex.current)
            return 100000000;
        if (activeStepIndex === 0)
            return 100000000;
        const prevCenter = getMapCenterAndZoom((_a = steps[activeStepIndex - 1]) === null || _a === void 0 ? void 0 : _a.chainStepActivities.map(({ activity: { site: { locationCoordinates }, }, }) => locationCoordinates), width);
        if (JSON.stringify(prevCenter) === JSON.stringify(activeStepCenter)) {
            // Same location
            return 0;
        }
        return distanceTo(prevCenter.center, activeStepCenter.center);
    }, [steps, activeStepIndex]);
    // Based on these steps the map will draw lines between each step.
    const stepsLinkedOnMap = useMemo(() => {
        if (isSummaryMap) {
            if (mapType === MapType.DEFAULT) {
                return steps; // all links should be visible when the summary map is enabled
            }
            else {
                return []; // no links should be visible in the summary mode when there's too many links ( Ie. Maptype is LIMITED_LINKS mode)
            }
        }
        if (activeStepIndex === 0)
            return [];
        const currentStep = steps[activeStepIndex];
        if (activeStepIndex === 1)
            return [currentStep];
        const previousStep = steps[activeStepIndex - 1];
        // Returns only the current step if the MapType is in LIMITED_LINKS mode
        // this  prevents too many lines from showing up because it's only the links from the current step that are showed.
        return mapType === MapType.DEFAULT
            ? [previousStep, currentStep]
            : [currentStep];
    }, [activeStepIndex, steps, isSummaryMap, mapType]);
    useEffect(() => {
        if (isSummaryMap) {
            // Centers the map around all the steps and zooms to the correct level so all steps are visible in one view.
            // Only does this for the summary map, when isSummaryMap is visible the map will only focus on a single step.
            // instead of simply using the center of each step to calculate the total center and zoom,
            // we use all the individual locations to ensure they are visible on the overview.
            const { center: centerAllCoords, zoom } = getMapCenterAndZoom(allStepActivities.map(({ activity: { site: { locationCoordinates }, }, }) => locationCoordinates), width, isSummaryMap);
            setViewport(Object.assign(Object.assign({}, viewport), { latitude: centerAllCoords.lat, longitude: centerAllCoords.lng, zoom: zoom }));
        }
    }, [isSummaryMap, width, allStepActivities]);
    const getZoomForFlyDistance = (flyDistance, boundsZoom) => {
        if (flyDistance < SUPER_NEARBY_DISTANCE) {
            return CLOSEST_ZOOM;
        }
        else if (flyDistance < NEARBY_DISTANCE) {
            return MINIMUM_ZOOM;
        }
        return boundsZoom;
    };
    useEffect(() => {
        // Centers the map on a single point. The direct opposite of the isSummaryMap behaviour.
        if (!isIntersecting || !mapLoaded || isSummaryMap)
            return;
        const delay = prevIndex.current === activeStepIndex ? 250 : 0;
        const { chainStepActivities } = steps[activeStepIndex];
        const zoom = Math.min(activeStepCenter.zoom, MINIMUM_ZOOM);
        const fly = (duration) => {
            setViewport(Object.assign(Object.assign({}, viewport), { longitude: activeStepCenter.center.lng, latitude: activeStepCenter.center.lat, zoom: chainStepActivities.length === 1
                    ? getZoomForFlyDistance(distanceToPrevious, zoom)
                    : zoom, transitionDuration: duration, 
                // @ts-ignore
                transitionInterpolator: distanceToPrevious === 0
                    ? new LinearInterpolator()
                    : new FlyToInterpolator(), transitionEasing: easePolyInOut }));
        };
        const flyTimeout = setTimeout(() => {
            fly(distanceToPrevious === 0
                ? 250
                : distanceToPrevious < NEARBY_DISTANCE
                    ? MAP_ANIMATION.SHORT
                    : MAP_ANIMATION.LONG);
        }, delay);
        return () => {
            clearTimeout(flyTimeout);
        };
    }, [
        activeStepIndex,
        activeStepCenter,
        width,
        isIntersecting,
        mapLoaded,
        isSummaryMap,
    ]);
    useEffect(() => {
        const progress = {
            coef: 0,
        };
        anime({
            duration: distanceToPrevious < NEARBY_DISTANCE
                ? MAP_ANIMATION.SHORT
                : MAP_ANIMATION.LONG,
            targets: progress,
            enterProgress: 1,
            coef: 1,
            easing: 'easeInOutCubic',
            update() {
                const animate = stepsLinkedOnMap.length > (layers || []).length;
                const newLayers = stepsLinkedOnMap
                    .map(({ id }, index) => {
                    // If the mapType is not default ->
                    // filter out all the links that are not linked to the activeNode
                    // This prevents lines from appearing which are also linked to other inactive map markers.
                    const visibleLinks = allStepActivityLinks.filter(({ targetActivity, stepId }) => id === stepId &&
                        (mapType === MapType.DEFAULT ||
                            targetActivity.id === activeStepActivity.id));
                    const pathLayer = createPathLayer({
                        data: visibleLinks,
                        stepId: id,
                        color: palette.primary.main,
                        coef: animate && index + 1 === steps.length ? progress.coef : 1,
                        getPath: (d) => {
                            const centerPoint = center(geometry('MultiPoint', [
                                [
                                    d.sourceActivity.locationCoordinates.lng,
                                    d.sourceActivity.locationCoordinates.lat,
                                ],
                                [
                                    d.targetActivity.locationCoordinates.lng,
                                    d.targetActivity.locationCoordinates.lat,
                                ],
                            ]));
                            return [
                                [
                                    d.sourceActivity.locationCoordinates.lng,
                                    d.sourceActivity.locationCoordinates.lat,
                                ],
                                [
                                    centerPoint.geometry.coordinates[0],
                                    centerPoint.geometry.coordinates[1],
                                ],
                                [
                                    d.targetActivity.locationCoordinates.lng,
                                    d.targetActivity.locationCoordinates.lat,
                                ],
                            ];
                        },
                        zoom: getZoomForFlyDistance(distanceToPrevious, 12),
                    });
                    // If any links have a transportation mode, we add a new layer for the the transportation mode icons.
                    if (!isSummaryMap &&
                        isBatchTracingActive &&
                        stepsLinkedOnMap.some(({ chainStepActivities }) => chainStepActivities.some(({ incomingLinks }) => incomingLinks.some(({ transportationMode }) => !!transportationMode &&
                            transportationMode !== ModeOfTransport.OTHER)))) {
                        const transportationModeLayer = new IconLayer({
                            id: `icon-layer-${id}`,
                            data: visibleLinks,
                            getIcon: (d) => MAP_TRANSPORT_ICONS[d.transportationMode],
                            getPosition: (d) => {
                                const centerPoint = center(geometry('MultiPoint', [
                                    [
                                        d.sourceActivity.locationCoordinates.lng,
                                        d.sourceActivity.locationCoordinates.lat,
                                    ],
                                    [
                                        d.targetActivity.locationCoordinates.lng,
                                        d.targetActivity.locationCoordinates.lat,
                                    ],
                                ]));
                                return [
                                    centerPoint.geometry.coordinates[0],
                                    centerPoint.geometry.coordinates[1],
                                ];
                            },
                            getSize: 16,
                            pickable: false,
                        });
                        return [pathLayer, transportationModeLayer];
                    }
                    return [pathLayer];
                })
                    .flat();
                setLayers(newLayers);
            },
        });
    }, [
        stepsLinkedOnMap,
        allStepActivityLinks,
        activeStepIndex,
        activeStepActivity,
    ]);
    return {
        viewport,
        layers,
        isCenterSameAsPrevious: distanceToPrevious === 0,
        isCenterNearbyPrevious: distanceToPrevious < NEARBY_DISTANCE,
    };
};
export default useMapViewport;
